import { Link, Navigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  CheckCircle2,
  ChevronRight,
  MapPin,
  Phone,
  ShieldCheck,
} from "lucide-react";

import SafeImage from "@/components/SafeImage";
import SiteLayout from "@/components/SiteLayout";
import SeoHead from "@/components/SeoHead";
import {
  BreadcrumbJsonLd,
  FaqJsonLd,
  ServiceJsonLd,
  WebPageJsonLd,
} from "@/components/JsonLd";
import { SITE } from "@/data/site";
import { getServiceBySlug, getServicePath, services } from "@/data/services";
import { homeSectionTo } from "@/utils/navigation";

export default function ServicePage() {
  const { slug } = useParams();
  const service = getServiceBySlug(slug);

  if (!service) {
    return <Navigate to="/" replace />;
  }

  const Icon = service.icon;
  const related = services.filter((s) => s.slug !== service.slug).slice(0, 3);

  return (
    <SiteLayout testId={`service-page-${service.slug}`}>
      <SeoHead
        title={service.metaTitle}
        description={service.metaDescription}
        keywords={service.keywords}
        path={getServicePath(service.slug)}
        image={service.image}
        type="article"
      />
      <BreadcrumbJsonLd service={service} />
      <ServiceJsonLd service={service} />
      <WebPageJsonLd service={service} />
      <FaqJsonLd faq={service.faq} />

      {/* ── Hero ── */}
      <section className="service-hero relative min-h-[70svh] sm:min-h-[75vh] flex items-end overflow-hidden">
        <SafeImage
          src={service.image}
          alt={service.imageAlt}
          className="absolute inset-0 w-full h-full object-cover object-center"
          loading="eager"
          fetchPriority="high"
          sizes="100vw"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#030812] via-[#030812]/75 to-[#030812]/30" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#030812]/90 via-[#030812]/40 to-transparent" />

        <div className="relative z-10 tw-container w-full pb-10 sm:pb-14 md:pb-16 pt-28 sm:pt-32">
          <nav aria-label="Breadcrumb" className="service-breadcrumb mb-5 sm:mb-6">
            <Link to="/">Početna</Link>
            <ChevronRight size={14} aria-hidden />
            <Link to={homeSectionTo("#services")}>Usluge</Link>
            <ChevronRight size={14} aria-hidden />
            <span aria-current="page">{service.breadcrumb || service.shortTitle}</span>
          </nav>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl"
          >
            <div className="overline">{service.tag}</div>
            <h1 className="mt-3 sm:mt-4 service-hero__title">{service.title}</h1>
            <p className="mt-4 sm:mt-6 text-[#c8d6ec] text-sm sm:text-base md:text-lg leading-relaxed max-w-2xl">
              {service.heroLead}
            </p>
            <div className="mt-6 sm:mt-8 flex flex-col sm:flex-row gap-3 sm:gap-4">
              <Link to={homeSectionTo("#contact")} className="tw-btn w-full sm:w-auto justify-center">
                Besplatna procena <ArrowRight size={16} strokeWidth={2.5} />
              </Link>
              <a href={SITE.phoneHref} className="tw-btn-ghost w-full sm:w-auto justify-center">
                <Phone size={16} /> {SITE.phone}
              </a>
            </div>
          </motion.div>

          <div className="service-highlights mt-8 sm:mt-10">
            {service.highlights.map((h) => (
              <div key={h.label} className="service-highlight">
                <span className="service-highlight__value">{h.value}</span>
                <span className="service-highlight__label">{h.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── SEO intro ── */}
      <section className="service-intro tw-section pt-8 sm:pt-12 pb-0">
        <div className="tw-container max-w-4xl">
          <p className="text-[#b8c9e4] text-sm sm:text-base md:text-lg leading-relaxed">
            {service.seoIntro}
          </p>
        </div>
      </section>

      {/* ── Targets + Inventory ── */}
      <section className="tw-section pt-8 sm:pt-12">
        <div className="tw-container grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="service-panel"
          >
            <div className="service-panel__icon">
              <Icon size={22} className="text-[#a7e2ff]" strokeWidth={1.8} />
            </div>
            <h2 className="service-panel__title">Za koga je usluga</h2>
            <div className="flex flex-wrap gap-2 mt-4">
              {service.targets.map((t) => (
                <span key={t} className="service-chip">{t}</span>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="service-panel"
          >
            <h2 className="service-panel__title">Šta peremo</h2>
            <ul className="mt-4 space-y-3">
              {service.inventory.map((item) => (
                <li key={item} className="flex items-start gap-3 text-sm sm:text-base text-[#c8d6ec]">
                  <CheckCircle2 size={18} className="text-[#55d0ff] shrink-0 mt-0.5" strokeWidth={2} />
                  {item}
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </section>

      {/* ── Process ── */}
      <section className="tw-section pt-0">
        <div className="tw-container">
          <div className="text-center mb-8 sm:mb-10">
            <div className="overline">Proces saradnje</div>
            <h2 className="mt-3 tw-heading-section text-2xl sm:text-3xl md:text-4xl">
              Kako izgleda{" "}
              <span className="blue-text font-display italic">vaša usluga</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {service.process.map((step, i) => (
              <motion.div
                key={step.step}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="process-card"
              >
                <span className="process-card__watermark">{step.step}</span>
                <h3 className="process-card__title relative z-10">{step.title}</h3>
                <p className="process-card__desc relative z-10">{step.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Content + Sidebar ── */}
      <section className="tw-section pt-0">
        <div className="tw-container grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-10">
          <div className="lg:col-span-7 space-y-8 md:space-y-10">
            {service.sections.map((section, i) => (
              <motion.article
                key={section.heading}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                className="service-content-block"
              >
                <h2 className="font-heading-md text-xl sm:text-2xl text-white">
                  {section.heading}
                </h2>
                <p className="mt-3 text-[#c8d6ec] text-sm sm:text-base leading-relaxed">
                  {section.body}
                </p>
              </motion.article>
            ))}

            {/* Local SEO */}
            <motion.article
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="service-local"
            >
              <div className="flex items-center gap-3 mb-4">
                <MapPin size={20} className="text-[#55d0ff]" />
                <h2 className="font-heading-md text-lg sm:text-xl text-white">
                  Usluga u Beogradu
                </h2>
              </div>
              <p className="text-[#a7bcd6] text-sm sm:text-base leading-relaxed">
                {service.localSeo}
              </p>
            </motion.article>

            {/* FAQ */}
            <article>
              <h2 className="font-heading-md text-xl sm:text-2xl text-white mb-5 sm:mb-6">
                Često postavljana pitanja
              </h2>
              <div className="space-y-3">
                {service.faq.map((item) => (
                  <details key={item.q} className="service-faq group">
                    <summary className="service-faq__q">
                      {item.q}
                      <ChevronRight size={18} className="service-faq__chevron shrink-0" />
                    </summary>
                    <p className="service-faq__a">{item.a}</p>
                  </details>
                ))}
              </div>
            </article>
          </div>

          <aside className="lg:col-span-5">
            <div className="service-sidebar lg:sticky lg:top-24">
              <div className="tw-card p-6 sm:p-8">
                <div className="flex items-center gap-3 mb-5">
                  <ShieldCheck size={22} className="text-[#55d0ff]" />
                  <div className="overline">Prednosti saradnje</div>
                </div>
                <ul className="space-y-3">
                  {service.benefits.map((benefit) => (
                    <li key={benefit} className="flex items-start gap-3 text-sm text-[#c8d6ec]">
                      <CheckCircle2 size={16} className="text-[#55d0ff] shrink-0 mt-0.5" strokeWidth={2} />
                      {benefit}
                    </li>
                  ))}
                </ul>
                <Link to={homeSectionTo("#contact")} className="tw-btn w-full mt-6 justify-center">
                  Zatražite ponudu
                </Link>
                <a
                  href={SITE.phoneHref}
                  className="mt-3 flex items-center justify-center gap-2 text-sm text-[#a7e2ff] hover:text-[#55d0ff] transition"
                >
                  <Phone size={16} />
                  {SITE.phone}
                </a>
              </div>
            </div>
          </aside>
        </div>
      </section>

      {/* ── Related ── */}
      <section className="tw-section pt-0 border-t border-[#55d0ff]/10">
        <div className="tw-container">
          <div className="overline mb-5 sm:mb-6">Povezane usluge</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {related.map((item) => (
              <Link
                key={item.slug}
                to={getServicePath(item.slug)}
                className="service-related group"
              >
                <div className="service-related__img">
                  <SafeImage
                    src={item.image}
                    alt={item.imageAlt}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    loading="lazy"
                    sizes="(max-width: 768px) 100vw, 33vw"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#030812] to-transparent" />
                </div>
                <div className="p-5 sm:p-6">
                  <h3 className="font-heading-md text-base sm:text-lg text-white group-hover:text-[#55d0ff] transition-colors">
                    {item.shortTitle}
                  </h3>
                  <p className="mt-2 text-[#a7bcd6] text-sm leading-relaxed line-clamp-2">
                    {item.cardDesc}
                  </p>
                  <span className="mt-3 inline-block text-[#55d0ff] text-sm">
                    Saznajte više →
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA band ── */}
      <section className="service-cta-band">
        <div className="tw-container text-center py-12 sm:py-16">
          <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl text-white">
            Spremni za besprekoran tekstil u vašem objektu?
          </h2>
          <p className="mt-4 text-[#b8c9e4] text-sm sm:text-base max-w-xl mx-auto">
            Kontaktirajte TOP WASH DOO za besplatnu procenu obima i personalizovanu B2B ponudu u Beogradu.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <Link to={homeSectionTo("#contact")} className="tw-btn justify-center">
              Započnite saradnju <ArrowRight size={16} strokeWidth={2.5} />
            </Link>
            <Link to={homeSectionTo("#services")} className="tw-btn-ghost justify-center">
              Sve usluge
            </Link>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}
